// Table renderer for Ask Your Data answer envelopes.
//
// The orchestrator caps responses at 500 rows server-side. We further
// truncate to MAX_ROWS for display and surface a "showing N of M"
// note. No virtualisation needed at this size; just MUI Table.

import React, { useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';

import { surfacesFor } from './uiTokens';

const MAX_ROWS = 200;


const inferColumns = (rows) => {
  const seen = new Map();
  rows.slice(0, 50).forEach((r) => {
    if (r && typeof r === 'object') {
      Object.keys(r).forEach((k) => {
        if (!seen.has(k)) seen.set(k, { key: k, label: k });
      });
    }
  });
  return Array.from(seen.values());
};


const formatCell = (value) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') {
    // Heuristic: trim long floats but keep ints.
    if (Number.isInteger(value)) return value.toLocaleString();
    return Math.abs(value) >= 1000
      ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  try { return JSON.stringify(value); } catch { return String(value); }
};


const AskDataTable = ({ table }) => {
  const rows    = Array.isArray(table?.rows) ? table.rows : [];
  const columns = useMemo(() => {
    const provided = Array.isArray(table?.columns) ? table.columns : [];
    if (provided.length) {
      return provided.map((c) => ({
        key:   c.key || c.field || c.id || String(c),
        label: c.label || c.name || c.key || String(c),
      }));
    }
    return inferColumns(rows);
  }, [table?.columns, rows]);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        (no rows)
      </Typography>
    );
  }

  const visible = rows.slice(0, MAX_ROWS);

  // Borderless, row-separated table - the attachment panel-card
  // already frames it, so no Paper outline here.
  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer sx={{ maxHeight: 360 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  sx={(t) => {
                    const s = surfacesFor(t);
                    return {
                      bgcolor: 'transparent',
                      fontWeight: 500, fontSize: 10.5,
                      textTransform: 'uppercase', letterSpacing: 0.6,
                      color: s.faint,
                      borderBottom: `1px solid ${s.border}`,
                      py: 0.75,
                    };
                  }}
                >
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row, idx) => (
              <TableRow key={idx} hover>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    sx={(t) => {
                      const s = surfacesFor(t);
                      return {
                        fontSize: 12.5,
                        color: s.text,
                        borderBottom: `1px solid ${s.border}`,
                        py: 1,
                      };
                    }}
                  >
                    {formatCell(row?.[c.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > visible.length && (
        <Typography
          sx={(t) => ({ display: 'block', mt: 0.75, fontSize: 11, color: surfacesFor(t).faint })}
        >
          Showing {visible.length} of {rows.length} rows.
        </Typography>
      )}
    </Box>
  );
};

export default AskDataTable;
